import type { StateCreator } from "zustand";
import {
  expenseApi,
  expenseListQuery,
  type ExpenseListParams,
} from "../../api/expense/api";
import type { Expense, ExpenseListResponse } from "../../expense/types";
import { isAbortError } from "./requests";

export type ExpenseListQueryState = {
  params: ExpenseListParams;
  items: Expense[];
  statusTotals: NonNullable<ExpenseListResponse["statusTotals"]>;
  nextCursor: string | null;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  fetchedAt: number | null;
  version: number;
};

export type ExpenseListCacheSlice = {
  expenseListQueries: Record<string, ExpenseListQueryState>;
  loadExpenseListQuery: (
    params: ExpenseListParams,
    force?: boolean,
  ) => Promise<void>;
  loadMoreExpenseListQuery: (params: ExpenseListParams) => Promise<void>;
};

type ExpenseListCacheStore = ExpenseListCacheSlice & {
  expenseQueryVersion: number;
};

type PendingRequest = { promise: Promise<void>; controller: AbortController };
const requests = new Map<string, PendingRequest>();
const moreRequests = new Map<string, PendingRequest>();
const MAX_CACHED_QUERIES = 12;
let cacheGeneration = 0;

export const expenseListCacheInitialState = {
  expenseListQueries: {},
} satisfies Pick<ExpenseListCacheSlice, "expenseListQueries">;

export const resetExpenseListCacheRequests = () => {
  requests.forEach(({ controller }) => controller.abort());
  moreRequests.forEach(({ controller }) => controller.abort());
  requests.clear();
  moreRequests.clear();
  cacheGeneration += 1;
};

export const expenseListQueryState = (
  params: ExpenseListParams,
  page: Pick<ExpenseListResponse, "items" | "statusTotals" | "nextCursor">,
  version: number,
): ExpenseListQueryState => ({
  params,
  items: page.items,
  statusTotals: page.statusTotals || [],
  nextCursor: page.nextCursor,
  loading: false,
  loadingMore: false,
  error: null,
  fetchedAt: Date.now(),
  version,
});

export const withExpenseListQuery = (
  queries: Record<string, ExpenseListQueryState>,
  key: string,
  query: ExpenseListQueryState,
) => {
  const next = { ...queries, [key]: query };
  const entries = Object.entries(next);
  if (entries.length <= MAX_CACHED_QUERIES) return next;
  return Object.fromEntries(
    entries
      .sort(([, left], [, right]) =>
        left.loading !== right.loading
          ? left.loading
            ? -1
            : 1
          : (right.fetchedAt || 0) - (left.fetchedAt || 0),
      )
      .slice(0, MAX_CACHED_QUERIES),
  );
};

export const createExpenseListCacheSlice: StateCreator<
  ExpenseListCacheStore,
  [],
  [],
  ExpenseListCacheSlice
> = (set, get) => ({
  ...expenseListCacheInitialState,
  loadExpenseListQuery: async (params, force = false) => {
    const key = expenseListQuery(params);
    const state = get();
    const cached = state.expenseListQueries[key];
    if (
      !force &&
      cached?.version === state.expenseQueryVersion &&
      !cached.error
    ) return;
    const pending = requests.get(key);
    if (pending) return pending.promise;
    const generation = cacheGeneration;
    const controller = new AbortController();

    set((latest) => ({
      expenseListQueries: withExpenseListQuery(latest.expenseListQueries, key, {
        params,
        items: cached?.items || [],
        statusTotals: cached?.statusTotals || [],
        nextCursor: cached?.nextCursor || null,
        loading: true,
        loadingMore: false,
        error: null,
        fetchedAt: cached?.fetchedAt || null,
        version: cached?.version ?? -1,
      }),
    }));

    const request = expenseApi
      .list(params, controller.signal)
      .then((page) => {
        if (generation !== cacheGeneration) return;
        set((latest) => ({
          expenseListQueries: withExpenseListQuery(
            latest.expenseListQueries,
            key,
            expenseListQueryState(params, page, latest.expenseQueryVersion),
          ),
        }));
      })
      .catch((error) => {
        if (generation !== cacheGeneration || isAbortError(error)) return;
        set((latest) => {
          const current = latest.expenseListQueries[key];
          if (!current) return {};
          return {
            expenseListQueries: withExpenseListQuery(latest.expenseListQueries, key, {
              ...current,
              loading: false,
              error:
                error instanceof Error
                  ? error.message
                  : "Unable to load expenses",
              version: latest.expenseQueryVersion,
            }),
          };
        });
        throw error;
      })
      .finally(() => {
        if (requests.get(key)?.promise === request) requests.delete(key);
      });
    requests.set(key, { promise: request, controller });
    return request;
  },
  loadMoreExpenseListQuery: async (params) => {
    const key = expenseListQuery(params);
    const current = get().expenseListQueries[key];
    if (!current?.nextCursor || current.loading || current.loadingMore) return;
    const pending = moreRequests.get(key);
    if (pending) return pending.promise;
    const generation = cacheGeneration;
    const controller = new AbortController();
    const cursor = current.nextCursor;
    set((state) => ({
      expenseListQueries: withExpenseListQuery(state.expenseListQueries, key, {
        ...current,
        loadingMore: true,
        error: null,
      }),
    }));

    const request = expenseApi
      .list({ ...params, cursor, includeTotal: false }, controller.signal)
      .then((page) => {
        if (generation !== cacheGeneration) return;
        set((state) => {
          const latest = state.expenseListQueries[key];
          if (!latest || latest.nextCursor !== cursor) return {};
          const existingIds = new Set(latest.items.map((item) => item.id));
          return {
            expenseListQueries: withExpenseListQuery(state.expenseListQueries, key, {
              ...latest,
              items: [
                ...latest.items,
                ...page.items.filter((item) => !existingIds.has(item.id)),
              ],
              nextCursor: page.nextCursor,
              loadingMore: false,
            }),
          };
        });
      })
      .catch((error) => {
        if (generation !== cacheGeneration || isAbortError(error)) return;
        set((state) => {
          const latest = state.expenseListQueries[key];
          if (!latest) return {};
          return {
            expenseListQueries: withExpenseListQuery(state.expenseListQueries, key, {
              ...latest,
              loadingMore: false,
              error:
                error instanceof Error
                  ? error.message
                  : "Unable to load more expenses",
            }),
          };
        });
        throw error;
      })
      .finally(() => {
        if (moreRequests.get(key)?.promise === request)
          moreRequests.delete(key);
      });
    moreRequests.set(key, { promise: request, controller });
    return request;
  },
});
